import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Plus, 
  ChevronLeft, 
  ChevronRight, 
  Calendar as CalendarIcon,
  List,
  Grid3x3,
  Copy,
  GripVertical 
} from "lucide-react";
import { useState } from "react";
import { format, startOfWeek, addDays, startOfMonth, endOfMonth, isSameDay } from "date-fns";
import { useScheduledPosts } from "@/hooks/useScheduledPosts";
import { CalendarInsights } from "./CalendarInsights";
import { BulkActionsBar } from "./BulkActionsBar";
import { DndContext, DragEndEvent, DragOverlay, useSensor, useSensors, PointerSensor } from "@dnd-kit/core";
import { PostWithAccount } from "@/hooks/useScheduledPosts";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type Post = PostWithAccount;

const platforms = {
  instagram: "bg-gradient-to-r from-purple-500 to-pink-500",
  twitter: "bg-blue-400",
  linkedin: "bg-blue-600",
  facebook: "bg-blue-500"
};

type ViewMode = "month" | "week" | "day";

interface EnhancedContentCalendarProps {
  onSchedulePost?: (date: Date) => void;
  showHeader?: boolean;
}

export const EnhancedContentCalendar = ({ onSchedulePost, showHeader = true }: EnhancedContentCalendarProps) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>("month");
  const [activePostId, setActivePostId] = useState<string | null>(null);
  
  const {
    posts,
    loading,
    selectedPosts,
    updatePostDate,
    bulkReschedule,
    bulkDelete,
    duplicatePost,
    togglePostSelection,
    selectAll,
    clearSelection,
  } = useScheduledPosts();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const getPostsForDate = (date: Date) => {
    return posts.filter(post => {
      if (!post.scheduled_for) return false;
      const postDate = new Date(post.scheduled_for);
      return isSameDay(postDate, date);
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) {
      setActivePostId(null);
      return;
    }

    const postId = active.id as string;
    const newDateStr = over.id as string;
    const newDate = new Date(newDateStr);

    updatePostDate(postId, newDate);
    setActivePostId(null);
  };

  const handlePrevious = () => {
    if (viewMode === "month") {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    } else if (viewMode === "week") {
      setCurrentDate(addDays(currentDate, -7));
    } else {
      setCurrentDate(addDays(currentDate, -1));
    }
  };

  const handleNext = () => {
    if (viewMode === "month") {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    } else if (viewMode === "week") {
      setCurrentDate(addDays(currentDate, 7));
    } else {
      setCurrentDate(addDays(currentDate, 1));
    }
  };

  const renderMonthView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const startDate = startOfWeek(monthStart);
    const days = [];
    let day = startDate;

    while (day <= monthEnd || days.length % 7 !== 0) {
      days.push(day);
      day = addDays(day, 1);
    }

    return (
      <div className="grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(dayName => (
          <div key={dayName} className="bg-muted p-3 text-center font-medium text-sm">
            {dayName}
          </div>
        ))}

        {days.map((day, idx) => {
          const dayPosts = getPostsForDate(day);
          const isCurrentMonth = day.getMonth() === currentDate.getMonth();
          const isToday = isSameDay(day, new Date());
          const dateStr = day.toISOString();

          return (
            <div
              key={idx}
              id={dateStr}
              className={`bg-card p-3 min-h-[120px] hover:bg-muted/30 transition-base ${
                !isCurrentMonth ? 'opacity-50' : ''
              }`}
            >
              <div className={`text-sm font-medium mb-2 ${isToday ? 'text-primary font-bold' : ''}`}>
                {format(day, 'd')}
                {isToday && <span className="ml-1 text-xs">(Today)</span>}
              </div>
              <div className="space-y-1">
                {dayPosts.map(post => {
                  const postDate = new Date(post.scheduled_for!);
                  const time = format(postDate, 'h:mm a');
                  const platform = (post.social_accounts as any)?.platform || 'instagram';
                  const isSelected = selectedPosts.has(post.id);
                  
                  return (
                    <div
                      key={post.id}
                      id={post.id}
                      className={`group relative text-xs p-2 rounded ${
                        platforms[platform as keyof typeof platforms] || 'bg-muted'
                      } text-white cursor-move hover:shadow-lg transition-all ${
                        isSelected ? 'ring-2 ring-white' : ''
                      }`}
                      draggable
                      onDragStart={() => setActivePostId(post.id)}
                    >
                      <div className="flex items-start gap-1">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => togglePostSelection(post.id)}
                          className="h-3 w-3 border-white data-[state=checked]:bg-white data-[state=checked]:text-primary"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{time}</div>
                          <div className="truncate opacity-90 text-[10px]">
                            {post.content.substring(0, 30)}...
                          </div>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button 
                              size="icon" 
                              variant="ghost" 
                              className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity text-white hover:bg-white/20"
                            >
                              <GripVertical className="h-3 w-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem onClick={() => duplicatePost(post.id)}>
                              <Copy className="h-4 w-4 mr-2" />
                              Duplicate
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderWeekView = () => {
    const weekStart = startOfWeek(currentDate);
    const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

    return (
      <div className="grid grid-cols-7 gap-2">
        {days.map((day) => {
          const dayPosts = getPostsForDate(day);
          const isToday = isSameDay(day, new Date());

          return (
            <div key={day.toISOString()} className="space-y-2">
              <div className={`text-center p-2 rounded-lg ${isToday ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                <div className="text-sm font-medium">{format(day, 'EEE')}</div>
                <div className="text-lg font-bold">{format(day, 'd')}</div>
              </div>
              <div className="space-y-2 min-h-[400px]">
                {dayPosts.map(post => {
                  const platform = (post.social_accounts as any)?.platform || 'instagram';
                  const isSelected = selectedPosts.has(post.id);
                  
                  return (
                    <Card
                      key={post.id}
                      className={`p-3 cursor-pointer ${isSelected ? 'ring-2 ring-primary' : ''}`}
                      onClick={() => togglePostSelection(post.id)}
                    >
                      <div className={`text-xs p-1 rounded w-fit ${platforms[platform as keyof typeof platforms]}`}>
                        <span className="text-white font-medium capitalize">{platform}</span>
                      </div>
                      <p className="text-justify text-xs mt-2 line-clamp-2">{post.content}</p>
                      <p className="text-justify text-xs text-muted-foreground mt-1">
                        {format(new Date(post.scheduled_for!), 'h:mm a')}
                      </p>
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderDayView = () => {
    const dayPosts = getPostsForDate(currentDate);
    const hours = Array.from({ length: 24 }, (_, i) => i);

    return (
      <div className="space-y-1">
        {hours.map(hour => {
          const hourPosts = dayPosts.filter(post => {
            const postHour = new Date(post.scheduled_for!).getHours();
            return postHour === hour;
          });

          return (
            <div key={hour} className="flex gap-2 min-h-[60px] border-b border-border">
              <div className="w-20 text-sm text-muted-foreground py-2">
                {format(new Date().setHours(hour, 0), 'h:mm a')}
              </div>
              <div className="flex-1 py-2 space-y-2">
                {hourPosts.map(post => {
                  const platform = (post.social_accounts as any)?.platform || 'instagram';
                  const isSelected = selectedPosts.has(post.id);
                  
                  return (
                    <Card
                      key={post.id}
                      className={`p-3 cursor-pointer ${isSelected ? 'ring-2 ring-primary' : ''}`}
                      onClick={() => togglePostSelection(post.id)}
                    >
                      <div className="flex items-start gap-2">
                        <div className={`text-xs p-1 rounded ${platforms[platform as keyof typeof platforms]}`}>
                          <span className="text-white font-medium capitalize">{platform}</span>
                        </div>
                        <div className="flex-1">
                          <p className="text-justify text-sm">{post.content}</p>
                          <p className="text-justify text-xs text-muted-foreground mt-1">
                            {format(new Date(post.scheduled_for!), 'h:mm a')}
                          </p>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return <div className="text-center p-8">Loading calendar...</div>;
  }

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          {showHeader ? (
            <div>
              <h2 className="text-2xl font-bold">Content Calendar</h2>
              <p className="text-muted-foreground">Schedule and manage your social media posts</p>
            </div>
          ) : <div />}
          <div className="flex items-center gap-2">
            {selectedPosts.size > 0 && (
              <Button variant="outline" onClick={selectAll} size="sm">
                Select All ({posts.length})
              </Button>
            )}
            <Button className="gradient-primary" onClick={() => onSchedulePost?.(new Date())}>
              <Plus className="w-4 h-4 mr-2" />
              Schedule Post
            </Button>
          </div>
        </div>

        {/* Calendar Insights */}
        <CalendarInsights posts={posts} currentMonth={currentDate} />

        {/* Calendar Card */}
        <Card className="p-6">
          {/* Header with View Controls */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <h3 className="text-xl font-semibold">
                {viewMode === "month" && format(currentDate, 'MMMM yyyy')}
                {viewMode === "week" && `Week of ${format(startOfWeek(currentDate), 'MMM d, yyyy')}`}
                {viewMode === "day" && format(currentDate, 'EEEE, MMMM d, yyyy')}
              </h3>
              <div className="flex gap-1 bg-muted rounded-lg p-1">
                <Button
                  variant={viewMode === "month" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("month")}
                >
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  Month
                </Button>
                <Button
                  variant={viewMode === "week" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("week")}
                >
                  <Grid3x3 className="w-4 h-4 mr-2" />
                  Week
                </Button>
                <Button
                  variant={viewMode === "day" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("day")}
                >
                  <List className="w-4 h-4 mr-2" />
                  Day
                </Button>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="icon" onClick={handlePrevious}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" onClick={() => setCurrentDate(new Date())}>
                Today
              </Button>
              <Button variant="outline" size="icon" onClick={handleNext}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Calendar Views */}
          {viewMode === "month" && renderMonthView()}
          {viewMode === "week" && renderWeekView()}
          {viewMode === "day" && renderDayView()}

          {/* Legend */}
          <div className="flex items-center gap-6 mt-6 pt-6 border-t border-border">
            <div className="text-sm font-medium">Platforms:</div>
            {Object.entries(platforms).map(([platform, color]) => (
              <div key={platform} className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded ${color}`} />
                <span className="text-sm capitalize">{platform}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Bulk Actions Bar */}
        <BulkActionsBar
          selectedCount={selectedPosts.size}
          onClearSelection={clearSelection}
          onBulkReschedule={(date) => bulkReschedule(Array.from(selectedPosts), date)}
          onBulkDelete={() => bulkDelete(Array.from(selectedPosts))}
        />

        {/* Drag Overlay */}
        <DragOverlay>
          {activePostId && (
            <div className="bg-primary text-white p-2 rounded shadow-lg">
              Dragging post...
            </div>
          )}
        </DragOverlay>
      </div>
    </DndContext>
  );
};
